<template>
  <div>
    <b-card header-tag="header">
      <template #header>
        <h4><i class="fas fa-clipboard" />&nbsp;Statistiques</h4>
      </template>
      <div>
        <b-row>
          <b-col cols="5">
            <b-form-datepicker
              class="mb-2"
              v-model="fromDate"
              reset-button
              locale="fr"
              placeholder="Date de debut"
              label-reset-button="Réinitialiser"
            ></b-form-datepicker>
          </b-col>
          <b-col cols="5">
            <b-form-datepicker
              class="mb-2"
              v-model="toDate"
              reset-button
              locale="fr"
              placeholder="Date de fin"
              label-reset-button="Réinitialiser"
            ></b-form-datepicker>
          </b-col>
          <b-col cols="2">
            <b-button style="float: right" @click="setDatesCurrentMonth"
              >Mois en cours</b-button
            >
          </b-col>
        </b-row>
        <CustomTable
          class="p-0 text-left"
          :items="statistics"
          :fields="fields"
          :filterable="true"
          :selectable="true"
          :current-page="currentPage"
          :per-page="perPage"
          :total-rows="totalRows"
          :tableOptions="tableOptions"
          :pageOptions="[10, 25, 50, 100]"
          @sort-changed="onSortChanged"
          @filtered="onFilterChanged"
          @page-changed="onPageChanged"
          @per-page-changed="onPerPageChanged"
        >
          <template #empty>
            <h4 class="text-secondary text-center mt-1">Aucune statistique</h4>
          </template>
        </CustomTable>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import CustomTable from '@/components/CustomTable.vue';
import get from 'lodash/get';

const ALARM_HISTORY_COLLECTION = 'alarm-history';

export default {
  name: 'Statistics',
  components: {
    CustomTable,
  },
  data() {
    return {
      statistics: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      roomId: null,
      filter: null,
      sort: null,
      fromDate: null,
      toDate: null,
      tableOptions: {
        'head-variant': 'light',
      },
      fields: [
        {
          key: 'sourceUserName',
          label: 'Utilisateur',
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'key',
          label: "ID de l'alarme",
          sortable: true,
          thClass: 'bg-success text-light',
        },
        {
          key: 'doc_count',
          label: "Nombre d'alarme",
          sortable: true,
          thClass: 'bg-success text-light',
        },
      ],
    };
  },

  computed: {
    ...mapGetters('tenants', ['selectedTenant', 'selectedSoftTenant']),
    residenceId() {
      return get(this.selectedSoftTenant, '_source.reference', null);
    },
  },

  async mounted() {
    await this.fetchStatistics();
  },

  methods: {
    buildFetchQuery() {
      let query = {
        query: {
          bool: {
            must: [],
          },
        },
        aggs: {
          group_by_sourceUserName: {
            terms: { field: 'sourceUserName.keyword' },
            aggs: {
              group_by_alarmReasonName: {
                terms: { field: 'alarmReasonName.keyword' },
              },
            },
          },
        },
      };

      if (this.residenceId) {
        query.query.bool.must.push({
          match: {
            reference: this.residenceId,
          },
        });
      }

      if (this.filter) {
        query.query.bool.must.push({
          multi_match: {
            query: this.filter,
            type: 'phrase_prefix',
            fields: ['*'],
          },
        });
      }

      if (this.fromDate || this.toDate) {
        query.query.bool.must.push({
          range: {
            datetime: {
              gt: this.fromDate
                ? moment(this.fromDate).format('yyyy-MM-DD HH:mm:ss')
                : null,
              lt: this.toDate
                ? moment(this.toDate).format('yyyy-MM-DD HH:mm:ss')
                : null,
            },
          },
        });
      }
      return query;
    },
    async fetchStatistics() {
      const query = this.buildFetchQuery();
      const statistics = [];

      try {
        const res = await this.$kuzzle.query({
          controller: 'pvstudio',
          action: 'userAlarms',
          index: this.selectedTenant.index,
          collection: ALARM_HISTORY_COLLECTION,
          query,
          from: (this.currentPage - 1) * this.perPage,
          size: this.perPage,
        });

        let totalRows = 0;
        for (
          let i = 0;
          i < res.result.aggregations.group_by_sourceUserName.buckets.length;
          i++
        ) {
          const bucket =
            res.result.aggregations.group_by_sourceUserName.buckets[i];
          bucket.group_by_alarmReasonName.buckets.forEach((alarm) => {
            totalRows++;
            if (
              totalRows >= this.currentPage * this.perPage - this.perPage &&
              statistics.length < this.currentPage * this.perPage
            ) {
              statistics.push({
                sourceUserName: bucket.key,
                key: alarm.key,
                doc_count: alarm.doc_count,
              });
            }
          });
        }

        if (this.sort) {
          statistics.sort((a, b) => {
            const fieldToSortOn = Object.keys(this.sort[0])[0];
            const order = this.sort[0][fieldToSortOn].order;

            if (order === 'asc') {
              return a[fieldToSortOn] > b[fieldToSortOn] ? 1 : -1;
            } else {
              return a[fieldToSortOn] < b[fieldToSortOn] ? 1 : -1;
            }
          });
        }

        this.statistics = statistics;
        this.totalRows = totalRows;
      } catch (error) {
        this.$log.error(error);
        this.showErrorToast(
          'Désolé, une erreur est survenue lors de la récupération des statistiques des alarmes.'
        );
      }
    },
    async subscribe() {
      if (this.roomId) {
        try {
          await this.$kuzzle.realtime.unsubscribe(this.roomId);
        } catch {
          // silent catch : there is no need to log error if user is not subscribed
        }

        this.roomId = null;
      }

      if (!this.statistics.length) {
        return;
      }

      this.roomId = await this.$kuzzle.realtime.subscribe(
        this.selectedTenant.index,
        ALARM_HISTORY_COLLECTION,
        {},
        async () => {
          await this.fetchStatistics();
        }
      );
    },
    async onPageChanged(page) {
      this.currentPage = page;
      await this.fetchStatistics();
    },
    async onPerPageChanged(perPage) {
      this.perPage = perPage;
      this.currentPage = 1;
      await this.fetchStatistics();
    },
    async onFilterChanged(filter) {
      this.filter = filter;
      await this.fetchStatistics();
    },
    async onSortChanged(sort) {
      this.sort = [
        {
          [sort.sortBy]: {
            order: sort.sortDesc ? 'desc' : 'asc',
          },
        },
      ];
      await this.fetchStatistics();
    },
    setDatesCurrentMonth() {
      const date = new Date();
      this.fromDate = new Date(date.getFullYear(), date.getMonth(), 1);
      this.toDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    },
    showErrorToast(message) {
      this.$bvToast.toast(message, {
        title: 'Erreur',
        variant: 'danger',
        toaster: 'b-toaster-bottom-right',
        appendToast: true,
        dismissible: true,
        noAutoHide: true,
      });
    },
  },

  watch: {
    async selectedSoftTenant() {
      await this.fetchStatistics();
    },
    async statistics() {
      await this.subscribe();
    },
    async fromDate() {
      await this.fetchStatistics();
    },
    async toDate() {
      await this.fetchStatistics();
    },
  },
};
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
.table {
  color: $primary;
}

.b-table-sticky-header {
  max-height: 60vh !important;
}
</style>
